import { ReactNode, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from '@/providers/LocationProvider';
import styles from './LocationGate.module.css';

type ScreenConfig = {
  title: string;
  body: string;
  buttonLabel?: string;
  showSpinner?: boolean;
};

const LocationScreen = ({
  title,
  body,
  buttonLabel,
  onAction,
  showSpinner = false,
}: ScreenConfig & { onAction?: () => void }) => (
  <div className={styles.page}>
    <Helmet>
      <title>Enable Location • AwkHerd</title>
    </Helmet>
    <div className={styles.card}>
      <h1>{title}</h1>
      <p className={styles.body}>{body}</p>
      {showSpinner && <div className={styles.spinner} role="status" aria-live="polite" />}
      {buttonLabel ? (
        <button type="button" onClick={onAction} className={styles.primaryButton}>
          {buttonLabel}
        </button>
      ) : null}
    </div>
  </div>
);

export const LocationGate = ({ children }: { children: ReactNode }) => {
  const { status, requestPermission, hasRequested, coords, error } = useLocation();

  useEffect(() => {
    if (status === 'error') {
      console.error('[LocationGate] Rendering error screen', { error });
    } else if (status === 'denied') {
      console.warn('[LocationGate] Rendering denied screen');
    }
  }, [status, error]);

  if (status === 'granted' && coords) {
    return <>{children}</>;
  }

  if (status === 'locating' || (hasRequested && status === 'idle')) {
    return (
      <LocationScreen
        title="Checking location…"
        body="Hang tight while we securely determine where you are so we can suggest nearby people."
        showSpinner
      />
    );
  }

  if (!hasRequested) {
    return (
      <LocationScreen
        title="AwkHerd needs your location"
        body="We use your location to find people nearby who share your interests. Turn on location services to continue."
        buttonLabel="Enable location"
        onAction={requestPermission}
      />
    );
  }

  if (status === 'denied') {
    return (
      <LocationScreen
        title="Location is disabled"
        body="AwkHerd can’t show nearby connections without location access. Enable location in your browser settings, then try again."
        buttonLabel="Enable location"
        onAction={requestPermission}
      />
    );
  }

  if (status === 'unsupported') {
    return (
      <LocationScreen
        title="Location not supported"
        body="This device or browser doesn’t support location services required for AwkHerd. Try updating your browser or switching devices."
      />
    );
  }

  if (status === 'error') {
    return (
      <LocationScreen
        title="We couldn’t access your location"
        body={
          error
            ? `${error}. Try enabling location and requesting access again.`
            : 'Try enabling location services and request access again.'
        }
        buttonLabel="Try again"
        onAction={requestPermission}
      />
    );
  }

  return (
    <LocationScreen
      title="AwkHerd needs your location"
      body="Turn on location services so we can connect you with nearby people."
      buttonLabel="Enable location"
      onAction={requestPermission}
    />
  );
};

export default LocationGate;
